/**
 * Layout - Apple 스타일 반응형 레이아웃
 * 상단 글로벌 네비게이션 + 메인 콘텐츠 + 풋터
 * Desktop / Tablet / Mobile 최적화
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// 파이터 아바타 색상
const AVATAR_COLORS = [
  'bg-red-500', 'bg-blue-500', 'bg-purple-500', 'bg-green-500',
  'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
];

export function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function FighterAvatar({ name, avatar, size = 'md', className = '' }) {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl',
  };
  const emojiSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-3xl',
  };

  const color = getAvatarColor(name);

  if (avatar) {
    return (
      <div className={`${sizes[size]} ${color} rounded-full flex items-center justify-center flex-shrink-0 ${className}`}>
        <span className={emojiSizes[size]}>{avatar}</span>
      </div>
    );
  }

  const initial = (name || '?').charAt(0).toUpperCase();

  return (
    <div className={`${sizes[size]} ${color} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}>
      {initial}
    </div>
  );
}

/* ─── 네비게이션 링크 정의 ─── */
const NAV_LINKS = [
  { label: 'AI 토론', path: '/battles' },
  { label: 'AI 비전', path: '/vision-lab' },
  { label: '농사직설 AI', path: '/farming' },
  { label: 'AI 도구', path: '/ai-tools' },
  { label: '구조센터', path: '/rescue-lab' },
  { label: '커뮤니티', path: '/community' },
  { label: '논문탐색', path: '/arxiv' },
  { label: '기술 포트폴리오', path: '/tech' },
  { label: 'AEGIS 시뮬레이터', path: '/aegis' },
  { label: '상품 쇼케이스', path: '/products' },
  { label: '고양이 언어모델', path: '/page-editor' },
  { label: 'CLI', path: '/cli' },
  { label: '묘냥의 숲', path: '/game/forest' },
  // mobidicsaju.com에서 이전된 기능 (2026-04-28)
  { label: '면접냥이', path: '/interview' },
  { label: '음연', path: '/musician' },
];

/* 상단 네비바에 직접 노출할 핵심 링크 (7개) */
const PRIMARY_NAV = NAV_LINKS.slice(0, 7);
/* 더보기 드롭다운에 들어갈 나머지 링크 */
const MORE_NAV = NAV_LINKS.slice(7);

const ADMIN_LINKS = [
  { label: 'ERP 대시보드', path: '/erp' },
  { label: '관제 센터', path: '/control-hub' },
  { label: '작전 센터', path: '/command-center' },
  { label: '배송 물류', path: '/dispatch-logistics' },
  { label: '디지털 트윈', path: '/digital-twin' },
  { label: 'The Bridge', path: '/the-bridge' },
  { label: 'Ops Console', path: '/ops-console' },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // 인증 페이지에서는 레이아웃 숨김
  const authPages = ['/login', '/register', '/verify-email', '/login-success'];
  const isAuthPage = authPages.some(p => location.pathname.startsWith(p));

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 모바일 메뉴 열릴 때 스크롤 잠금
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // 라우트 변경 시 모바일 메뉴 닫기
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  if (isAuthPage) return <>{children}</>;

  const isActive = (path) => location.pathname === path;
  // 투명 네비는 다크 히어로가 있는 홈에서만 — 라이트 페이지에서는 글라스 네비 유지
  const isHome = location.pathname === '/';

  return (
    <div className="min-h-screen bg-[#000]">
      {/* ─── 다크 글라스 글로벌 네비게이션 ─── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-[rgba(0,4,14,0.7)] backdrop-blur-[40px] backdrop-saturate-[180%] border-b border-white/[0.13] shadow-[0_4px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.13)]'
          : isHome
          ? 'bg-transparent'
          : 'bg-[rgba(0,4,14,0.7)] backdrop-blur-[40px] backdrop-saturate-[180%] border-b border-white/[0.13] shadow-[0_4px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.13)]'
      }`}>
        {/* Liquid Glass shimmer */}
        {(scrolled || mobileMenuOpen || !isHome) && (
          <>
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" aria-hidden />
            <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none" aria-hidden />
          </>
        )}
        <div className="max-w-apple-wide mx-auto px-6 h-12 flex items-center justify-between">
          {/* 로고 */}
          <Link to="/" className="flex-shrink-0">
            <span className="text-[16px] font-bold tracking-tight text-white">AI Fighter</span>
          </Link>

          {/* 데스크탑 네비게이션 */}
          <div className="hidden lg:flex items-center gap-5">
            {PRIMARY_NAV.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[13px] transition-colors duration-200 whitespace-nowrap ${isActive(link.path) ? 'text-white font-medium' : 'text-white/60 hover:text-white'}`}
              >
                {link.label}
              </Link>
            ))}
            {/* 더보기 드롭다운 */}
            <div className="relative group">
              <button className={`text-[13px] transition-colors duration-200 whitespace-nowrap ${MORE_NAV.some(l => isActive(l.path)) ? 'text-white font-medium' : 'text-white/60 hover:text-white'}`}>
                더보기 ▾
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-6 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-[rgba(6,8,24,0.92)] backdrop-blur-[32px] backdrop-saturate-[150%] rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.1)] border border-white/[0.12] py-1.5 w-48">
                  {MORE_NAV.map(link => (
                    <Link key={link.path} to={link.path}
                      className={`block px-4 py-2 text-[13px] transition-colors ${isActive(link.path) ? 'text-white font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 우측 아이콘 */}
          <div className="flex items-center gap-3">
            {/* 검색 */}
            <button onClick={() => navigate('/search')} className="text-white/60 hover:text-white transition p-1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>

            {/* 유저 메뉴 (데스크탑) */}
            <div className="hidden lg:block relative group">
              <button className="text-white/60 hover:text-white transition p-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </button>
              <div className="absolute right-0 top-8 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-[rgba(6,8,24,0.92)] backdrop-blur-[32px] backdrop-saturate-[150%] rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.1)] border border-white/[0.12] py-2 w-52">
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-2.5 border-b border-white/10">
                        <p className="text-sm font-semibold text-white">{user?.name || user?.nickname}</p>
                        <p className="text-xs text-white/40">{user?.email}</p>
                      </div>
                      <Link to="/mypage" className="block px-4 py-2 text-sm text-white/60 hover:bg-white/5 hover:text-white transition">마이페이지</Link>
                      <Link to="/erp" className="block px-4 py-2 text-sm text-white/60 hover:bg-white/5 hover:text-white transition">내 이용 기록</Link>
                      <Link to="/battles/create" className="block px-4 py-2 text-sm text-white/60 hover:bg-white/5 hover:text-white transition">새 토론 만들기</Link>
                      <Link to="/integrated-center" className="block px-4 py-2 text-sm text-white/60 hover:bg-white/5 hover:text-white transition">구조·입양 센터</Link>
                      {user?.isAdmin && (
                        <>
                          <div className="border-t border-white/10 my-1" />
                          {ADMIN_LINKS.map(link => (
                            <Link key={link.path} to={link.path} className="block px-4 py-2 text-sm text-white/60 hover:bg-white/5 hover:text-white transition">{link.label}</Link>
                          ))}
                        </>
                      )}
                      <div className="border-t border-white/10 my-1" />
                      <button onClick={() => { logout(); navigate('/'); }} className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition">로그아웃</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="block px-4 py-2.5 text-sm font-medium text-[#06b6d4] hover:bg-white/5 transition">로그인</Link>
                      <Link to="/register" className="block px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 transition">회원가입</Link>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 모바일 햄버거 */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white/60 hover:text-white transition p-1"
            >
              {mobileMenuOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* ─── 모바일 풀스크린 메뉴 ─── */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-12 bg-[rgba(0,4,14,0.94)] backdrop-blur-[48px] backdrop-saturate-[200%] z-[60] overflow-y-auto">
          <div className="px-6 py-8 space-y-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-2xl font-semibold transition-colors ${isActive(link.path) ? 'text-white' : 'text-white/40 hover:text-white'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-white/10 my-4 pt-4">
              <Link to="/search" className="block py-3 text-2xl font-semibold text-white/40 hover:text-white transition">검색</Link>
              <Link to="/integrated-center" className="block py-3 text-2xl font-semibold text-white/40 hover:text-white transition">구조·입양 센터</Link>
            </div>
            {isAuthenticated ? (
              <div className="border-t border-white/10 my-4 pt-4">
                <Link to="/mypage" className="block py-3 text-lg font-medium text-white/50 hover:text-white transition">마이페이지</Link>
                <Link to="/erp" className="block py-3 text-lg font-medium text-white/50 hover:text-white transition">내 이용 기록</Link>
                <Link to="/battles/create" className="block py-3 text-lg font-medium text-white/50 hover:text-white transition">새 토론 만들기</Link>
                {user?.isAdmin && ADMIN_LINKS.map(link => (
                  <Link key={link.path} to={link.path} className="block py-3 text-lg font-medium text-white/50 hover:text-white transition">{link.label}</Link>
                ))}
                <button onClick={() => { logout(); navigate('/'); setMobileMenuOpen(false); }} className="block py-3 text-lg font-medium text-red-400 hover:text-red-300 transition">로그아웃</button>
              </div>
            ) : (
              <div className="border-t border-white/10 my-4 pt-4">
                <Link to="/login" className="block py-3 text-lg font-semibold text-[#06b6d4] hover:text-cyan-300 transition">로그인</Link>
                <Link to="/register" className="block py-3 text-lg font-medium text-white/50 hover:text-white transition">회원가입</Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── 메인 콘텐츠 ─── */}
      <main className={isHome ? 'pb-16 lg:pb-0' : 'pt-12 pb-16 lg:pb-0'}>
        {children}
      </main>

      {/* ─── 모바일 하단 탭 바 ─── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[rgba(0,4,14,0.85)] backdrop-blur-[32px] backdrop-saturate-[180%] border-t border-white/[0.1] shadow-[0_-4px_24px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] safe-area-bottom">
        <div className="flex items-center justify-around h-14">
          {[
            { path: '/', label: '홈', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
            { path: '/battles', label: 'AI 토론', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="4" width="9" height="7" rx="1.5"/><rect x="13" y="9" width="9" height="7" rx="1.5"/><path d="M6.5 11v3l-2 2"/><path d="M17.5 16v2l2 2"/></svg> },
            { path: '/ai-tools', label: 'AI 도구', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/></svg> },
            { path: '/community', label: '커뮤니티', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="3"/><circle cx="6" cy="11" r="2"/><circle cx="18" cy="11" r="2"/><path d="M8 18c0-2.2 1.8-4 4-4s4 1.8 4 4"/></svg> },
            { path: '/mypage', label: '마이', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
          ].map(tab => (
            <Link key={tab.path} to={tab.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${
                location.pathname === tab.path ? 'text-[#06b6d4]' : 'text-white/30 hover:text-white/60'
              }`}>
              {tab.icon}
              <span className="text-[10px] font-medium">{tab.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* ─── 다크 풋터 (Liquid Glass) ─── */}
      <footer className="relative bg-[rgba(4,6,18,0.9)] backdrop-blur-[32px] backdrop-saturate-[180%] border-t border-white/[0.08] overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent pointer-events-none" aria-hidden />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-cyan-500/[0.02] to-transparent pointer-events-none" aria-hidden />
        <div className="max-w-apple-wide mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-[12px] font-semibold text-white mb-3">AI 서비스</h4>
              <ul className="space-y-2">
                <li><Link to="/battles" className="text-[12px] text-white/40 hover:text-white transition">AI 토론</Link></li>
                <li><Link to="/vision-lab" className="text-[12px] text-white/40 hover:text-white transition">AI 비전 랩</Link></li>
                <li><Link to="/farming" className="text-[12px] text-white/40 hover:text-white transition">농사직설 AI</Link></li>
                <li><Link to="/ai-tools" className="text-[12px] text-white/40 hover:text-white transition">AI 도구</Link></li>
                <li><Link to="/arxiv" className="text-[12px] text-white/40 hover:text-white transition">논문탐색</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[12px] font-semibold text-white mb-3">커뮤니티</h4>
              <ul className="space-y-2">
                <li><Link to="/community" className="text-[12px] text-white/40 hover:text-white transition">자유 게시판</Link></li>
                <li><Link to="/rescue-lab" className="text-[12px] text-white/40 hover:text-white transition">유기동물 구조</Link></li>
                <li><Link to="/integrated-center" className="text-[12px] text-white/40 hover:text-white transition">구조·입양 센터</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[12px] font-semibold text-white mb-3">계정</h4>
              <ul className="space-y-2">
                <li><Link to="/mypage" className="text-[12px] text-white/40 hover:text-white transition">마이페이지</Link></li>
                <li><Link to={isAuthenticated ? '/battles/create' : '/login'} className="text-[12px] text-white/40 hover:text-white transition">새 토론 만들기</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[12px] font-semibold text-white mb-3">비즈니스</h4>
              <ul className="space-y-2">
                <li><span className="text-[12px] text-white/40 cursor-pointer hover:text-white transition" onClick={() => { window.location.href = 'tel:' + atob('MDEwLTIxNTQtNDc2OQ=='); }}>{atob('MDEwLTIxNTQtNDc2OQ==')}</span></li>
                <li><span className="text-[12px] text-white/40 cursor-pointer hover:text-white transition" onClick={() => { window.location.href = 'mailto:' + atob('bWFzdGVyQHNrdGVjLmty'); }}>{atob('bWFzdGVyQHNrdGVjLmty')}</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/[0.08] pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <p className="text-[11px] text-white/20">&copy; 2026 Mobidic Company. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="text-[11px] text-white/20 hover:text-white/50 transition">이용약관</Link>
              <Link to="/privacy" className="text-[11px] text-white/20 hover:text-white/50 transition">개인정보처리방침</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
